<template>
  <div v-if="showContinue" style="z-index: 12000">
    <OnexDialog
      type="warning"
      :is-persistent="true"
      v-model="showContinue"
      max-width="480"
      :show-close-button="false"
      class="aboveDialog"
    >
      <template #title>
        <span>{{ $t('L_timeout_warning') }}</span>
      </template>
      <template #text>
        <span>{{
          `${$t('L_your_session_will_expire_in_30_seconds')} ${$t(
            'L_would_you_like_to_extend_your_session'
          )}`
        }}</span>
        <slot name="message"></slot>
      </template>
      <template #yesBtn>
        <span @click="continueSession">{{ $t('L_extend_session') }}</span>
      </template>
      <template #noBtn>
        <span @click="closeSession">{{ $t('L_end_session') }}</span>
      </template>
    </OnexDialog>
  </div>
</template>

<script lang="ts">
import { Session } from '@/shared/util/session';
import { lockVault } from '@/plugins/auth/utils/sessionManager';
import { CapacitorFacade } from '@/plugins/capacitorPlugin';
import { mdiAlarm } from '@mdi/js';
import { defineComponent, type ComponentOptions } from 'vue';
import { OnexButton, OnexDialog } from '@primerica/onex-ui-lib';
import { RouteNames } from '@/router/routeNames';
import * as sessionManager from '@/plugins/auth/utils/sessionManager.ts';
import { UnlockMode } from '@/plugins/auth/utils/definitions';

const componentOptions: ComponentOptions = {
  name: 'SessionDialog',
  components: {
    OnexButton,
    OnexDialog,
  },
  props: {
    externalPage: {
      type: Boolean,
      required: true,
      default: false,
    },
  },
  data: () => ({
    intervalId: null,
    showContinue: false,
    mdiAlarm,
  }),
  computed: {
    isMobileApp() {
      return CapacitorFacade.isIos() || CapacitorFacade.isAndroid();
    },
  },
  mounted() {
    this.startTimer();
  },

  methods: {
    startTimer() {
      Session.resetExpiration();
      this.intervalId = window.setInterval(
        Session.timer(this.warnCallback, this.closeSession),
        5000 // check session status every 5 seconds
      );
    },
    async signOut() {
      this.stopTimer();
      if (this.isMobileApp) {
        await this.$auth.logout(); // ! clear browser session and revoke tokens
        await this.$router.push({ name: RouteNames.LOGIN_LANDING_PAGE });
      } else {
        this.$auth.logout(); // ! clear browser session and revoke tokens
      }
    },
    stopTimer() {
      if (this.intervalId !== null) {
        window.clearInterval(this.intervalId);
        this.intervalId = null;
      }
    },
    async closeSession() {
      this.stopTimer();
      const isNativePlatform = await this.$pc2capacitor.isNativePlatform();
      if (isNativePlatform) {
        try {
          const unlockMode = await sessionManager.getUnlockMode();
          if (
            unlockMode?.valueOf() === UnlockMode.biometrics ||
            unlockMode?.valueOf() === UnlockMode.biometricsWithPasscode ||
            unlockMode?.valueOf() === UnlockMode.systemPasscode
          ) {
            await lockVault();
            await this.$router.push({ name: RouteNames.LOGIN_LANDING_PAGE });
          } else if (unlockMode?.valueOf() === UnlockMode.secureStorage) {
            await this.$auth.logout(); // ! clear browser session and revoke tokens
            await this.$router.push({ name: RouteNames.DASHBOARD });
          }
        } catch (_e: unknown) {
          this.$dynatrace.customError('SessionDialog', 'Error during session lock/logout', _e);
          await this.$auth.logout(); // ! clear browser session and revoke tokens
          await this.$router.push({ name: RouteNames.DASHBOARD });
        }
      } else {
        this.$auth.logout(); // ! clear browser session and revoke tokens
      }
    },
    warnCallback() {
      if (!this.externalPage) {
        this.showContinue = true;
      }
    },
    continueSession() {
      Session.resetExpiration();
      this.showContinue = false;
    },
  },
};
export default defineComponent(componentOptions);
</script>

<style lang="scss">
.v-dialog {
  .v-card__title {
    font-weight: 500;
  }
  .v-card__text {
    font-weight: 500;
  }
  .v-btn--small {
    font-size: 14px;
  }
}
</style>
